import cn from "classnames";
import { useNavigate } from "react-router-dom";
import { memo, useEffect, useState } from "react";
import {
  authenticationApi,
  AuthService,
  CURRENT_AUTH_ZONE,
  monolithApi,
  notificationsApi,
  NotificationsApiTypes,
} from "@rshbintech.dboul/http";
import { analytics } from "@rshbintech.dboul/analytics";
import {
  Overlay,
  Text,
  useMediaQuery,
  Dropdown,
} from "@rshbintech.dboul/core-ui";
import {
  Bell as BellIcon,
  Exit as ExitIcon,
  User as UserIcon,
  Burger as BurgerIcon,
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
  ChevronDown as ChevronDownIcon,
} from "@rshbintech.dboul/icons";
import {
  Logo as LogoImage,
  LogoNewYear as LogoNewYearImage,
} from "@rshbintech.dboul/images";
import {
  USER_ROLES,
  useRoles,
  useBankForBanksIndication,
} from "@rshbintech.dboul/access-control";
import { redirectToChooseOrganizationPage } from "../../auth-provider/utils";
import { CHOOSE_ORGANIZATION_PATHNAME } from "../constants";
import { ChangeLanguageButton } from "../../common/components/change-language-button";
import { useAppTranslation, useDefaultLanguage } from "../../hooks";
import {
  SNOW_PLOW_ANALYTICS_EVENT_CATEGORIES,
  SNOW_PLOW_ANALYTIC_EVENTS,
} from "../../common/constants";
import { Chat as ChatIconButton } from "./chat";
import { Notifications } from "./notifications";
import {
  HEADER_METRIC_NAMES,
  PROFILE_PAGE_PATHNAME,
  NOTIFICATIONS_POPUP_TRANSITION_DURATION,
} from "./constants";
import { HeaderProps } from "./types";
import { isNewYearPeriod } from "./utils";
import styles from "./styles.scss";

export const Header = memo(
  ({
    userName,
    clientName,
    clientSettings,
    isMenuOpen,
    mainPagePath,
    onHeaderIconClick,
  }: HeaderProps) => {
    const navigate = useNavigate();
    const { hasRoleThatContains } = useRoles();
    const { isBankForBanksUser } = useBankForBanksIndication();
    const { translate } = useAppTranslation("LAYOUT");

    useDefaultLanguage();

    const [notificationsList, setNotificationsList] = useState<
      NotificationsApiTypes.NotificationItemResponse[] | null
    >(null);

    const [isNotificationsPopupOpened, setNotificationsPopupOpened] =
      useState(false);

    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [isProfileInfoButtonClicked, setIsProfileInfoButtonClicked] =
      useState(false);

    const [isDropDownChevronClicked, setIsDropDownChevronClicked] =
      useState(false);

    const handleCloseNotificationsPopup = () => {
      setNotificationsPopupOpened(false);
    };

    const handleExitNotificationsPopup = () => {
      setNotificationsList(null);
    };

    const getNotifications = async () => {
      try {
        const notificationsResponse = await notificationsApi.api.getAll({
          read: false,
        });

        setNotificationsList(notificationsResponse.data.items);
      } catch {
        setNotificationsList(null);
      }
    };

    const readNotifications = async () => {
      if (!notificationsList) {
        return;
      }

      const notificationIds = notificationsList.map(
        (notification) => notification.id
      );

      try {
        await notificationsApi.api.markChanged(
          {
            notificationIds,
          },
          { read: true }
        );
      } catch {
        /* TODO: Уточнить действия в случае ошибки прочитанности уведомлений */
      }
    };

    const closeMenuIfOpen = () => {
      if (!isMenuOpen) {
        return;
      }

      onHeaderIconClick();
    };

    const handleLogoClick = async () => {
      try {
        await analytics.sendEventAndWait(HEADER_METRIC_NAMES.LOGO_CLICK);
      } finally {
        window.location.href = mainPagePath;
      }
    };

    const handleNotificationsIconClick = () => {
      closeMenuIfOpen();

      setNotificationsPopupOpened(true);
      readNotifications();
      analytics.sendEvent(HEADER_METRIC_NAMES.NOTIFICATIONS_CLICK);
    };

    const handleLogoutIconClick = async () => {
      try {
        await analytics.sendEventAndWait(HEADER_METRIC_NAMES.LOGOUT_CLICK);
      } catch (error) {
        /* TODO: Добавить отправку отчета об ошибке */
      }

      try {
        if (AuthService.hasCurrentAuthZone) {
          await authenticationApi.api.logout();
        } else {
          await monolithApi.auth.jwtLogoutCreate();
        }
      } catch (error) {
        // TODO: Уточнить у дизайнеров как обрабатывать ошибку логаута на UI (http://sdlc.go.rshbank.ru/jira/browse/OB_COREUI-103).
      }
    };

    useEffect(() => {
      if (!notificationsList) {
        getNotifications();
      }
    }, [notificationsList]);

    const isMobile = useMediaQuery("mobile");
    const isTablet = useMediaQuery("tablet");
    const isDesktop = useMediaQuery("desktop");

    const menuIcon = isTablet ? <ChevronRightIcon /> : <BurgerIcon />;

    const isNeedToShowName =
      (userName || clientName) && (isTablet || isDesktop);

    const notificationsPopupTransitionName = isMobile ? "fade" : null;

    const handleProfileIconClick = () => {
      closeMenuIfOpen();

      analytics.sendEvent(HEADER_METRIC_NAMES.PROFILE_CLICK);
      navigate(PROFILE_PAGE_PATHNAME);
    };

    const handleChooseOrganizationPageClick = async () => {
      try {
        const { data } = await authenticationApi.api.returnToUser();

        if (data?.access_token) {
          await AuthService.setAccessToken(data.access_token);
          localStorage.setItem(CURRENT_AUTH_ZONE, CHOOSE_ORGANIZATION_PATHNAME);
          redirectToChooseOrganizationPage();
        }
      } catch (error) {}
    };

    const dropDownItems = [
      {
        content: translate("HEADER.DROP_DOWN_ITEMS.PROFILE"),
        dataTestId: "profile-info-dropdown-item-lk",
        onClick: handleProfileIconClick,
      },
      {
        content: translate("HEADER.DROP_DOWN_ITEMS.CHOOSE_ORGANIZATION"),
        dataTestId: "profile-info-dropdown-item-choose-organization",
        onClick: handleChooseOrganizationPageClick,
      },
    ];

    const toggleDropdownVisibility = () => {
      setIsDropdownVisible((isDropdownVisible) => !isDropdownVisible);
      setIsProfileInfoButtonClicked(true);
    };

    const toggleIsDropDownChevronClicked = () =>
      setIsDropDownChevronClicked((isClicked) => !isClicked);

    const handlerOnOutsideClick = () =>
      !isProfileInfoButtonClicked
        ? setIsDropdownVisible(false)
        : setIsProfileInfoButtonClicked(false);

    useEffect(() => {
      if (isDropDownChevronClicked) {
        setIsProfileInfoButtonClicked(false);
        setIsDropDownChevronClicked(false);
      }
    }, [isDropDownChevronClicked]);

    const chooseOrganizationChevron = (
      <Dropdown
        items={dropDownItems}
        shown={isDropdownVisible}
        onOutsideClick={handlerOnOutsideClick}
      >
        <div onClick={toggleIsDropDownChevronClicked}>
          <ChevronDownIcon
            size="large"
            className={cn(styles["choose-organization-chevron-icon"], {
              [styles.up]: isDropdownVisible,
            })}
          />
        </div>
      </Dropdown>
    );

    const handleProfileInfoButtonClick = AuthService.currentAuthZone
      ? toggleDropdownVisibility
      : handleProfileIconClick;

    const dropdownUserIcon = isNeedToShowName ? (
      <UserIcon />
    ) : (
      <Dropdown
        items={dropDownItems}
        shown={isDropdownVisible}
        align="right"
        onOutsideClick={handlerOnOutsideClick}
      >
        <div onClick={toggleIsDropDownChevronClicked}>
          <UserIcon />
        </div>
      </Dropdown>
    );

    return (
      <nav data-test-id="header" className={styles.header}>
        {(isMobile || isTablet) && (
          <button
            className={styles["menu-icon-container"]}
            onClick={onHeaderIconClick}
            data-test-id="menu-icon-container"
          >
            {isMenuOpen ? <CloseIcon /> : menuIcon}
          </button>
        )}

        <button
          className={cn(styles["logo-image-container"], {
            [styles["logo-image-container-new-year"]]: isNewYearPeriod(),
          })}
          onClick={handleLogoClick}
          data-test-id="logo-image"
          data-rptracker-event="click"
          data-rptracker-category="dboHeader"
          data-rptracker-action="menuLogoClick"
        >
          {isNewYearPeriod() ? (
            <LogoNewYearImage
              alt="new-year-logo-image"
              className={styles["new-year-logo-image"]}
            />
          ) : (
            <LogoImage alt="logo-image" className={styles["logo-image"]} />
          )}
        </button>
        <button
          className={cn(styles["profile-info-container"], {
            [styles["without-name"]]: !isNeedToShowName,
            [styles["without-background"]]: !isDropdownVisible,
          })}
          data-test-id="profile-info-container"
          data-rptracker-event="click"
          data-rptracker-category="dboHeader"
          data-rptracker-action="menuProfileClick"
          onClick={handleProfileInfoButtonClick}
        >
          {isNeedToShowName && (
            <div className={styles["profile-info"]} data-test-id="profile-info">
              <div className={styles["profile-info-client-name"]}>
                {clientName && (
                  <Text weight="medium" className={styles.name}>
                    {clientName}
                  </Text>
                )}
                {AuthService.currentAuthZone ? chooseOrganizationChevron : null}
              </div>
              {userName && <Text className={styles.name}>{userName}</Text>}
            </div>
          )}

          <div
            data-test-id="user-icon-container"
            className={styles["user-icon-container"]}
          >
            {AuthService.currentAuthZone ? dropdownUserIcon : <UserIcon />}
          </div>
        </button>

        <ChatIconButton clientSettings={clientSettings} />
        {isBankForBanksUser && (
          <div className={styles["language-change-container"]}>
            <ChangeLanguageButton />
          </div>
        )}
        {!isBankForBanksUser &&
          hasRoleThatContains(USER_ROLES.UI_DBO_NOTIFICATION_VIEW) && (
            <button
              className={cn(styles["notifications-icon-container"], {
                [styles.active]: isNotificationsPopupOpened,
              })}
              data-test-id="notifications-icon-container"
              onClick={handleNotificationsIconClick}
              data-rptracker-event="click"
              data-rptracker-category={
                SNOW_PLOW_ANALYTICS_EVENT_CATEGORIES.DBO_NOTIFICATIONS
              }
              data-rptracker-action={
                SNOW_PLOW_ANALYTIC_EVENTS.OPEN_NOTIFICATIONS
              }
              data-rptracker-value=""
            >
              <BellIcon />
              {notificationsList &&
                notificationsList.length > 0 &&
                !isNotificationsPopupOpened && (
                  <span className={styles["unread-notifications-marker"]} />
                )}
            </button>
          )}
        <button
          className={styles["logout-icon-container"]}
          data-test-id="logout-icon-container"
          onClick={handleLogoutIconClick}
          data-rptracker-event="click"
          data-rptracker-category="dboLogOut"
          data-rptracker-action="logoutManual"
          data-rptracker-value=""
        >
          <ExitIcon />
        </button>
        <Overlay
          transitionName={notificationsPopupTransitionName}
          transitionDuration={NOTIFICATIONS_POPUP_TRANSITION_DURATION}
          className={styles.overlay}
          onClose={handleCloseNotificationsPopup}
          onExited={handleExitNotificationsPopup}
          shouldCloseByBackdropClick
          opened={isNotificationsPopupOpened}
        >
          <Notifications
            className={
              isMobile
                ? cn({
                    [styles["popup-entering"]]: isNotificationsPopupOpened,
                    [styles["popup-exiting"]]: !isNotificationsPopupOpened,
                  })
                : null
            }
            clientSettings={clientSettings}
            notificationsList={notificationsList || []}
            onCloseNotificationsPopup={handleCloseNotificationsPopup}
          />
        </Overlay>
      </nav>
    );
  }
);
